import React from 'react';
import { Check, DoorOpen, X } from 'lucide-react';
import SchemaModal from '../../../ui/SchemaModal';
import EmptyState from '../../../ui/EmptyState';
import CardListViewer from './CardListViewer';

/**
 * Rückfragen und Meldungen des Bretts, die der Server auslöst.
 *
 *   <PlaytestDialogs state={state} dispatch={dispatch}
 *     actions={{ respondViewHand, respondCardControl }} onLeaveToEntry={() => navigate('/playtest')} />
 *
 * - Hand-Anfrage (`state.handViewRequest`): „Annehmen" zeigt dem Anfragenden die eigene Hand,
 *   „Ablehnen" nicht. Schließen auf anderem Weg (✕, Escape, Zurück-Geste) gilt als Ablehnen —
 *   sonst wartete der Anfragende auf eine Antwort, die nie kommt.
 * - Kontroll-Anfrage (`state.controlRequest`): dasselbe für die Kontrolle über eine Brettkarte.
 * - Brett geschlossen (`state.boardClosed { message, code }`): Modal ohne Schließen per
 *   Backdrop; jeder Weg hinaus führt über `onLeaveToEntry` zum Einstieg, auf dem Brett ist
 *   nichts mehr zu tun.
 * - Aufgedeckte Hand (`state.revealedHand`): Raster im `CardListViewer` (Titel „Hand von …", nur Ansehen);
 *   eine leere Hand zeigt dort den Leerzustand. Schließen räumt den Zustand per `HAND_REVEALED_CLEARED`.
 * - Bestätigungen für „Ich bin raus", Zurücksetzen und Beenden hängen an den Einträgen der
 *   Aktionsleiste (`useActionConfirm`), nicht hier.
 */
export default function PlaytestDialogs({ state, actions = {}, dispatch, onLeaveToEntry }) {
  const { handViewRequest, controlRequest, boardClosed } = state;

  const answerHandView = (accepted) => {
    if (!handViewRequest) return;
    actions.respondViewHand?.(handViewRequest.requesterId, accepted);
    dispatch?.({ type: 'HAND_VIEW_REQUEST_CLEARED' });
  };

  const answerControl = (accepted) => {
    if (!controlRequest) return;
    actions.respondCardControl?.(controlRequest.requesterId, controlRequest.instanceId, accepted);
    dispatch?.({ type: 'CONTROL_REQUEST_CLEARED' });
  };

  return (
    <>
      <RequestDialog
        open={!!handViewRequest}
        title="Hand-Einsicht"
        description={`${handViewRequest?.requesterName || 'Ein Teilnehmer'} möchte deine Hand sehen.`}
        onAnswer={answerHandView}
      />
      <RequestDialog
        open={!!controlRequest}
        title="Kontrolle abgeben"
        description={`${controlRequest?.requesterName || 'Ein Teilnehmer'} möchte die Kontrolle über ${controlRequest?.cardName || 'eine Karte'} übernehmen.`}
        onAnswer={answerControl}
      />
      <RevealedHand revealedHand={state.revealedHand} dispatch={dispatch} />
      <SchemaModal
        open={!!boardClosed}
        onOpenChange={(open) => { if (!open) onLeaveToEntry?.(); }}
        size="sm"
        title={boardClosed?.code === 'SESSION_ENDED' ? 'Session beendet' : 'Brett geschlossen'}
        dismissOnBackdrop={false}
      >
        <EmptyState
          icon={DoorOpen}
          title={boardClosed?.message || 'Dieses Brett ist nicht mehr verfügbar.'}
          action={(
            <button type="button" className="btn-primary" onClick={() => onLeaveToEntry?.()}>
              Zur Playtest-Übersicht
            </button>
          )}
        />
      </SchemaModal>
    </>
  );
}

function RequestDialog({ open, title, description, onAnswer }) {
  return (
    <SchemaModal
      open={open}
      onOpenChange={(next) => { if (!next) onAnswer(false); }}
      size="sm"
      title={title}
      description={description}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={() => onAnswer(false)}>
            <X size={16} aria-hidden /> Ablehnen
          </button>
          <button type="button" className="btn-primary" onClick={() => onAnswer(true)}>
            <Check size={16} aria-hidden /> Annehmen
          </button>
        </>
      )}
    />
  );
}

function RevealedHand({ revealedHand, dispatch }) {
  const owner = revealedHand?.targetName || 'Teilnehmer';
  return (
    <CardListViewer
      open={!!revealedHand}
      onOpenChange={(open) => { if (!open) dispatch?.({ type: 'HAND_REVEALED_CLEARED' }); }}
      title={`Hand von ${owner}`}
      zone="hand"
      emptyTitle="Keine Karten auf der Hand"
      cards={revealedHand?.cards || []}
    />
  );
}
